/**
 * The Mail search box: chips, the words after them, and the suggestion menu (logic/mailQuery; the same keys as
 * SearchViewModel's menu). The box only holds the text: `state.query` is what the screen hands MailboxViewModel.search.
 * People for `from:` / `to:` come from the open list and, once a letter is typed, the workspace's contacts.
 *
 * Recent searches: focusing an empty box lists mine, up to eight, and the ones that fit appear while typing. A search is
 * saved on Enter or when one of its results opens (`remember`), never per keystroke. They live on the server, per person
 * (`listMailSearchHistory`), read each time the box is focused so another device's show up.
 */
import type { MailContact, MailSearchEntry } from "@bundle/contracts";
import { applySearchSuggestion, type SearchSuggestMenu, type SearchTextEdit } from "../logic/searchSuggest.ts";
import {
  commitMailChip,
  formatMailQuery,
  mailChipOrs,
  mailQueryChips,
  mailRecentChips,
  mailSearchSuggestions,
  matchingMailSearches,
  setMailChipMode,
  type MailChip,
  type MailChipMode,
} from "../logic/mailQuery.ts";
import type { MailboxViewModel } from "./MailboxViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** A saved search, ready to draw: its filters as chips, then its words. */
export type MailRecentRow = { id: string; query: string; chips: string[]; text: string };

export type MailSearchViewState = {
  chips: MailChip[];
  /** Whether each chip reads "or" with the one before it. */
  ors: boolean[];
  /** The text field. */
  draft: string;
  /** Chips and draft as one query. */
  query: string;
  suggest?: SearchSuggestMenu & { active: number };
  /** The box is open on its history (focused, empty or typing). */
  recentOpen: boolean;
  /** Searches to offer: the latest on an empty box, those that fit while typing. */
  recent: MailRecentRow[];
  /** The row ↑ ↓ are on, -1 for none. */
  recentActive: number;
};

const CONTACTS_DEBOUNCE_MS = 150;

export class MailSearchViewModel extends ViewModel<MailSearchViewState> {
  private caret = 0;
  private dismissed = false;
  private contacts: MailContact[] = [];
  private contactsFor: string | undefined;
  private ticket = 0;
  private cancelTimer: (() => void) | undefined;
  private history: MailSearchEntry[] = [];

  constructor(
    private readonly mail: MailboxViewModel,
    private readonly ws: WorkspaceViewModel,
  ) {
    super({ chips: [], ors: [], draft: "", query: "", recentOpen: false, recent: [], recentActive: -1 });
    this.onDispose(() => this.cancelTimer?.());
  }

  /** Shows a query the box didn't build (a link, or the list's own when the box opens). */
  load(query: string): void {
    if (query === this.state.query) return;
    const { chips, draft } = mailQueryChips(query);
    this.caret = draft.length;
    this.dismissed = true;
    this.put(chips, draft);
  }

  /** The field changed. A complete filter and a space become a chip. */
  setDraft(draft: string): void {
    this.dismissed = false;
    const committed = commitMailChip(draft, this.state.chips);
    if (committed !== undefined) {
      this.commit(committed);
      return;
    }
    this.caret = draft.length;
    this.put(this.state.chips, draft);
  }

  setCaret(caret: number): void {
    const c = Math.max(0, Math.min(caret, this.state.draft.length));
    if (c === this.caret) return;
    this.caret = c;
    this.dismissed = false;
    this.refreshSuggest();
  }

  /** Focus: the empty box offers every filter. */
  focus(): void {
    this.dismissed = false;
    this.refreshSuggest();
    void this.loadHistory();
  }

  dismissSuggest(): void {
    this.dismissed = true;
    if (this.state.suggest !== undefined) this.update({ suggest: undefined });
    this.refreshRecent();
  }

  /** A search that was run: Enter, or a result opened. The same one again moves to the top. */
  remember(): void {
    const query = this.state.query.trim();
    if (query === "") return;
    this.ws.deps.api.saveMailSearch(this.ws.wsId, query).then(
      (entry) => {
        if (this.isDisposed) return;
        this.history = [entry, ...this.history.filter((e) => e.id !== entry.id)];
        this.refreshRecent();
      },
      () => undefined, // A search still ran; it just isn't kept.
    );
  }

  /** A Recent row chosen: the box takes it and it runs. */
  chooseRecent(id: string): void {
    const entry = this.history.find((e) => e.id === id);
    if (entry === undefined) return;
    const { chips, draft } = mailQueryChips(entry.query);
    this.caret = draft.length;
    this.dismissed = true;
    this.put(chips, draft);
    this.remember();
  }

  removeRecent(id: string): void {
    this.history = this.history.filter((e) => e.id !== id);
    this.refreshRecent();
    void this.ws.deps.api.deleteMailSearch(this.ws.wsId, id).catch(() => this.loadHistory());
  }

  clearRecent(): void {
    this.history = [];
    this.refreshRecent();
    void this.ws.deps.api.clearMailSearchHistory(this.ws.wsId).catch(() => this.loadHistory());
  }

  /** A key from the footer ("from:") goes into the field, ready for its value. */
  insertKey(key: string): void {
    const draft = `${this.state.draft}${this.state.draft === "" || this.state.draft.endsWith(" ") ? "" : " "}${key}`;
    this.setDraft(draft);
  }

  private async loadHistory(): Promise<void> {
    try {
      this.history = await this.ws.deps.api.listMailSearchHistory(this.ws.wsId);
      if (!this.isDisposed) this.refreshRecent();
    } catch {
      // The box works without it.
    }
  }

  highlightSuggest(index: number): void {
    const s = this.state.suggest;
    if (s === undefined || index < 0 || index >= s.items.length || index === s.active) return;
    this.update({ suggest: { ...s, active: index } });
  }

  /** ↑ ↓ Enter Tab Esc, and Backspace on an empty field (removes the last chip). `handled`: the field does nothing. */
  key(key: "up" | "down" | "enter" | "tab" | "escape" | "backspace"): boolean {
    if (key === "backspace") {
      if (this.state.draft !== "" || this.state.chips.length === 0) return false;
      this.put(this.state.chips.slice(0, -1), "");
      return true;
    }
    const s = this.state.suggest;
    const rows = this.state.recentOpen ? this.state.recent : [];
    if ((key === "up" || key === "down") && s === undefined && rows.length > 0) {
      const n = rows.length;
      this.update({ recentActive: key === "up" ? (this.state.recentActive <= 0 ? n - 1 : this.state.recentActive - 1) : (this.state.recentActive + 1) % n });
      return true;
    }
    if (key === "enter" && this.state.recentActive >= 0 && rows[this.state.recentActive] !== undefined) {
      this.chooseRecent(rows[this.state.recentActive]!.id);
      return true;
    }
    if (key === "escape" && this.state.recentOpen && s === undefined) {
      this.dismissSuggest();
      return true;
    }
    if (key === "enter" && s === undefined) {
      const committed = commitMailChip(`${this.state.draft} `, this.state.chips);
      if (committed !== undefined) this.commit(committed);
      this.remember();
      this.dismissSuggest();
      return committed !== undefined;
    }
    if (s === undefined) return false;
    if (key === "escape") {
      this.dismissSuggest();
      return true;
    }
    if (key === "up" || key === "down") {
      const n = s.items.length;
      this.update({ suggest: { ...s, active: (s.active + (key === "up" ? -1 : 1) + n) % n } });
      return true;
    }
    return this.chooseSuggest(s.active) !== undefined;
  }

  chooseSuggest(index: number): SearchTextEdit | undefined {
    const s = this.state.suggest;
    const item = s?.items[index];
    if (s === undefined || item === undefined) return undefined;
    const edit = applySearchSuggestion(this.state.draft, s, item);
    const committed = commitMailChip(edit.text, this.state.chips);
    if (committed !== undefined) {
      this.commit(committed);
      return { text: committed.draft, caret: committed.draft.length };
    }
    this.caret = edit.caret;
    this.put(this.state.chips, edit.text);
    return edit;
  }

  removeChip(index: number): void {
    this.put(this.state.chips.filter((_, i) => i !== index), this.state.draft);
  }

  setChipMode(index: number, mode: MailChipMode): void {
    this.put(this.state.chips.map((c, i) => (i === index ? setMailChipMode(c, mode) : c)), this.state.draft);
  }

  clear(): void {
    this.caret = 0;
    this.dismissed = true;
    this.put([], "");
  }

  /** A filter became a chip: the results show, not the menu, until the next keystroke. */
  private commit(next: { chips: MailChip[]; draft: string }): void {
    this.caret = next.draft.length;
    this.dismissed = true;
    this.put(next.chips, next.draft);
  }

  private put(chips: MailChip[], draft: string): void {
    this.update({ chips, ors: mailChipOrs(chips), draft, query: formatMailQuery(chips, draft) });
    this.refreshSuggest();
  }

  private refreshSuggest(): void {
    let suggest: MailSearchViewState["suggest"];
    if (!this.dismissed) {
      const menu = this.menu();
      if (menu !== undefined) {
        const prev = this.state.suggest;
        const kept = prev === undefined ? -1 : menu.items.findIndex((i) => prev.items[prev.active]?.insert === i.insert);
        suggest = { ...menu, active: Math.max(0, kept) };
      }
    }
    if (suggest !== undefined || this.state.suggest !== undefined) this.update({ suggest });
    this.refreshRecent();
    this.wantContacts();
  }

  /** The Recent list for what's in the box. An empty box shows it whatever history holds: its note and footer teach. */
  private refreshRecent(): void {
    const empty = this.state.query.trim() === "";
    const typed = this.state.chips.length === 0 ? this.state.draft : this.state.query;
    const recent = this.dismissed ? [] : matchingMailSearches(this.history, typed).map((e) => ({ id: e.id, query: e.query, ...mailRecentChips(e.query) }));
    this.update({ recent, recentOpen: !this.dismissed && (empty || recent.length > 0), recentActive: -1 });
  }

  private menu(): SearchSuggestMenu | undefined {
    const people = [...this.mail.state.threads.flatMap((t) => t.participants), ...this.contacts.map((c) => ({ name: c.name, address: c.email }))];
    const now = this.ws.deps.clock.now();
    const today = new Date(now + this.ws.deps.clock.tzOffsetMinutes(now) * 60_000).toISOString().slice(0, 10);
    // The empty box opens on Recent, whose footer holds the keys.
    if (this.state.draft === "") return undefined;
    return mailSearchSuggestions(this.state.draft, Math.min(this.caret, this.state.draft.length), { labels: this.mail.state.labels, mailboxes: this.mail.state.mailboxes, people, today });
  }

  /** The word being typed after `from:`, `to:`, `with:` or `domain:`, looked up among the contacts. */
  private wantContacts(): void {
    const m = /(?:^|\s)-?(?:from|to|with|domain):=?(\S+)$/iu.exec(this.state.draft.slice(0, this.caret));
    const q = m?.[1];
    if (q === undefined || q === this.contactsFor) return;
    this.contactsFor = q;
    this.cancelTimer?.();
    const ticket = ++this.ticket;
    this.cancelTimer = this.ws.deps.clock.setTimer(CONTACTS_DEBOUNCE_MS, () => {
      this.ws.deps.api.listContacts(this.ws.wsId, undefined, q).then(
        (contacts) => {
          if (ticket !== this.ticket || this.isDisposed) return;
          this.contacts = contacts;
          this.refreshSuggest();
        },
        () => undefined, // The menu keeps what the open list offers.
      );
    });
  }
}
